
import { EMPTY_PARTICIPATION_RATE_ENTITY, ParticipationRateEntity } from "domain/entity/ParticipationRate/ParticipationRateEntity";
import { EMPTY_PARTICIPATION_RATE_SEARCH_CRITERIA, ParticipationRateSearchCriteria } from "domain/entity/ParticipationRate/ParticipationRateSearchCriteria";
import { ConsortiumRepository } from "domain/repository/ChargeDetailMaintenance/ConsortiumRepo";
import { ParticipationRateRepository } from "domain/repository/ParticipationRate/ParticipationRateRepo";
import _ from "lodash";
import { CntrStatusDroOpts } from "presentation/constant/DropDownOptions/Company/CntrStatusDroOpts";
import { ParticipationRateMaintenanceModel } from "presentation/model/ParticipationRate/ParticipationRateMaintenanceModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface ParticipationRateMaintenanceVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<ParticipationRateMaintenanceModel>> | ((value: SetStateAction<ParticipationRateMaintenanceModel>) => void),
    ],
    repo: ParticipationRateRepository,
    consortiumRepo: ConsortiumRepository,
}

export const ParticipationRateMaintenanceVM = ({ dispatch, repo, consortiumRepo }: ParticipationRateMaintenanceVMProps) => {
    const [participationRateMainDispatch] = dispatch;

    const loadDropdownOption = async () => {
        participationRateMainDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                cntrStatusDropdownOptions: CntrStatusDroOpts().getDroOptsModel(),
            }
        }))
        await consortiumRepo.getAllConsortiums().then(
            consortiums => {
                let consortiumCodeDropdownOptions = consortiums?.map((item) => ({
                    dropdownLabel: item.consortiumCode,
                    tagLabel: item.consortiumCode,
                    value: item.consortiumCode,
                })) ?? []
                consortiumCodeDropdownOptions = _.orderBy(consortiumCodeDropdownOptions, "dropdownLabel");

                participationRateMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        consortiumCodeDropdownOptions: consortiumCodeDropdownOptions,
                    }
                }))
            }
        );
    }

    const onSearch = async (isClick: boolean, searchCriteria: ParticipationRateSearchCriteria) => {
        participationRateMainDispatch(prevState => ({
            ...prevState,
            tableData: [],
        }))
        const entities = await repo.getEntities(searchCriteria);

        participationRateMainDispatch(prevState => ({
            ...prevState,
            tableData: entities,
            selectedRows: [],
            searchCriteria: searchCriteria,
            currentSelectedRow: EMPTY_PARTICIPATION_RATE_ENTITY,
            isBackMaster: false,
            isShowSearchClick: isClick,
        }))
    }

    const updateSelectedRows = async (rows: ParticipationRateEntity[]) => {
        participationRateMainDispatch(prevState => {
            let edit = false;
            if (prevState.isShowEditPanel && rows.length === 1 && rows[0].id !== prevState.currentSelectedRow.id) {
                edit = true;
            } else if (prevState.isShowEditPanel && rows.length !== 1) {
                edit = true;
            }
            return {
                ...prevState,
                selectedRows: rows,
                isShowEditPanel: edit ? !prevState.isShowEditPanel : prevState.isShowEditPanel,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onEdit = (currentEntity: ParticipationRateEntity) => {
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                currentSelectedRow: currentEntity,
                editingEntity: currentEntity,
                isShowEditPanel: true,
                isEdit: false,
                isRead: true,
            }
        })
    }

    const onEditClick = () => {
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                isRead: false,
                isEdit: true,
            }
        })
    }
    const onResetClick = () => {
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                editingEntity: prevState.currentSelectedRow
            }
        })
    }


    const onClose = () => {
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                currentSelectedRow: EMPTY_PARTICIPATION_RATE_ENTITY,
                selectedRows: [],
                editingEntity: EMPTY_PARTICIPATION_RATE_ENTITY,
                isBackMaster: true,
                isShowEditPanel: false,
                isRead: true,
                isEdit: false,
            }
        })
    }

    const onSaveClicked = () => {
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onSave = async (currentEntity: ParticipationRateEntity) => {
        return await repo.updateEntity(currentEntity);
    }

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        participationRateMainDispatch(prevState => ({
            ...prevState,
            editingEntity: {
                ...prevState.editingEntity,
                [fieldName]: checked ? "Y" : "N",
            }
        }))
    }

    const onFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                editingEntity: {
                    ...prevState.editingEntity,
                    [fieldKey]: val,
                },
                allFormState: {
                    ...prevState.masterState.allFormState,
                    [fieldKey]: '',
                }
            }
        })
    }

    const onSearchDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        participationRateMainDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            },
        }))
    }

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        participationRateMainDispatch(prevState => ({
            ...prevState,
            editingEntity: {
                ...prevState.editingEntity,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            },
        }))
    }

    const onApply = async (rows: ParticipationRateEntity[]) => {
        return await repo.applyEntity(rows);
    }

    const onCancel = () => {
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                isShowEditPanel: false,
            }
        })
    }

    const onSearchCriteriaResetClick = async () => {
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: { ...EMPTY_PARTICIPATION_RATE_SEARCH_CRITERIA },
            }
        })
    }

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (val) {
            val = val.toUpperCase();
        }
        participationRateMainDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                },
            }
        })
    }

    const onSearchCoVslVoyChange = (inputData: { co?: string, vsl?: string, voy?: string }, fieldName: { co: string, vsl: string, voy: string }) => {
        let coVal: string = "";
        let vslVal: string = "";
        let voyVal: string = "";
        if (inputData?.co) {
            coVal = inputData?.co.toUpperCase().replace(/\s+/g, '');
        }
        if (inputData?.vsl) {
            vslVal = inputData?.vsl.toUpperCase().replace(/\s+/g, '');
        }
        if (inputData?.voy) {
            voyVal = inputData?.voy.toUpperCase();
        }

        participationRateMainDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [fieldName.co]: coVal,
                [fieldName.vsl]: vslVal,
                [fieldName.voy]: voyVal,
            }
        }))
    }

    const onSearchClick = async () => {
        participationRateMainDispatch(prevState => {

            return {
                ...prevState,
                isShowSearchPanel: !prevState.isShowSearchPanel,
            }
        })
    }

    const onSearchClickCheck = async () => {
        participationRateMainDispatch(prevState => {

            return {
                ...prevState,
                isShowSearchClick: false,
            }
        })
    }

    return {
        onSearchClickCheck: onSearchClickCheck,
        onSearchClick: onSearchClick,
        onSearchCoVslVoyChange: onSearchCoVslVoyChange,
        onSearchFieldChange: onSearchFieldChange,
        onSearchCriteriaResetClick: onSearchCriteriaResetClick,
        onSearchDateRangeChange: onSearchDateRangeChange,
        onDateRangeChange: onDateRangeChange,
        loadDropdownOption: loadDropdownOption,
        updateSelectedRows: updateSelectedRows,
        onEdit: onEdit,
        onEditClick: onEditClick,
        onClose: onClose,
        onSearch: onSearch,
        onSaveClicked: onSaveClicked,
        onSave: onSave,
        onCheckboxChange: onCheckboxChange,
        onFieldChange: onFieldChange,
        onApply: onApply,
        onCancel: onCancel,
        onResetClick: onResetClick,
    }
}